import { assertSome, noop } from '@crocodille/util';
import { Map as MapboxMap, MapboxOptions } from 'mapbox-gl';
import {
  createContext,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { useIsomorphicLayoutEffect as useLayoutEffect } from './use-isomorphic-layout-effect';

export function useMapbox(
  containerRef: RefObject<HTMLElement | null>,
  options: Omit<MapboxOptions, 'container'> = {},
) {
  const mapRef = useRef<MapboxMap>(null);
  const mapLoadedRef = useRef(false);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useLayoutEffect(() => {
    if (!containerRef.current) {
      return;
    }

    mapRef.current = new MapboxMap({
      container: containerRef.current,
      ...optionsRef.current,
    });

    const runQueuedEffects = () => {
      mapLoadedRef.current = true;

      if (onLoadQueueRef.current.length === 0) {
        return;
      }

      const effects = onLoadQueueRef.current;
      onLoadQueueRef.current = [];

      effects.forEach((cb) => cb());

      runQueuedEffects();
    };

    mapRef.current.on('load', runQueuedEffects);

    return () => {
      mapRef.current?.off('load', runQueuedEffects);
      mapRef.current?.remove();
      mapRef.current = null;
      mapLoadedRef.current = false;
    };
  }, [containerRef]);

  const onLoadQueueRef = useRef<(() => void)[]>([]);

  const scheduleOnLoad = useCallback((cb: () => void) => {
    if (mapLoadedRef.current) {
      cb();

      return noop;
    } else {
      onLoadQueueRef.current.push(cb);

      return () => {
        onLoadQueueRef.current = onLoadQueueRef.current.filter(
          (fn) => fn !== cb,
        );
      };
    }
  }, []);

  return { mapRef, scheduleOnLoad };
}

type MapBoxContextValue = ReturnType<typeof useMapbox>;
const MapBoxContext = createContext<MapBoxContextValue | null>(null);

export const MapBoxProvider = MapBoxContext.Provider;

export function useMapboxEffect(
  effect: (map: MapboxMap) => (() => void) | void,
  deps: readonly unknown[],
) {
  const { mapRef, scheduleOnLoad } = assertSome(useContext(MapBoxContext));
  const effectRef = useRef(effect);
  effectRef.current = effect;

  useEffect(() => {
    let cleanupLogic = noop;
    let cancelScheduled = noop;

    const runEffect = () => {
      const map = mapRef.current;

      if (!map) {
        return;
      }

      cleanupLogic = effectRef.current(map) ?? noop;
    };

    cancelScheduled = scheduleOnLoad(runEffect);

    return () => {
      cancelScheduled();

      const map = mapRef.current;

      if (!map) {
        return;
      }

      if (map.loaded()) {
        cleanupLogic();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, scheduleOnLoad, ...deps]);
}
